import { getJwtToken } from '../config/cognito'

const pythonBaseUrl = () => (process.env.REACT_APP_PYTHON_API_URL || '').replace(/\/$/, '')

export class PythonApiError extends Error {
  status: number
  detail: unknown
  constructor(status: number, message: string, detail?: unknown) {
    super(message)
    this.status = status
    this.detail = detail
    this.name = 'PythonApiError'
  }
}

function messageFromDetail(detail: unknown, fallback: string): string {
  if (detail == null) return fallback
  if (typeof detail === 'string') return detail
  if (typeof detail === 'object' && detail !== null && 'message' in detail) {
    const msg = (detail as { message?: unknown }).message
    if (typeof msg === 'string' && msg.trim()) return msg
  }
  if (typeof detail === 'object') {
    try {
      return JSON.stringify(detail)
    } catch {
      return fallback
    }
  }
  return String(detail)
}

function joinUrl(path: string): string {
  const base = pythonBaseUrl()
  if (!base) {
    throw new PythonApiError(0, 'REACT_APP_PYTHON_API_URL is not set')
  }
  const p = path.startsWith('/') ? path : `/${path}`
  return `${base}${p}`
}

function isFormData(body: RequestInit['body']): boolean {
  return typeof FormData !== 'undefined' && body instanceof FormData
}

/**
 * Authenticated request to the mechanical Python API (Cognito ID token, same as Node `apiFetch`).
 */
export async function pythonApiFetch<T = unknown>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const token = await getJwtToken()
  if (!token) {
    throw new PythonApiError(401, 'Not authenticated')
  }
  const url = joinUrl(path)
  const form = isFormData(options.body)
  const res = await fetch(url, {
    ...options,
    headers: {
      ...(form ? {} : { 'Content-Type': 'application/json' }),
      Authorization: `Bearer ${token}`,
      ...(options.headers as Record<string, string>)
    }
  })
  if (!res.ok) {
    const text = await res.text()
    let detail: unknown = text || res.statusText
    let msg = text || res.statusText
    try {
      const j = JSON.parse(text) as { detail?: unknown }
      if (j && typeof j === 'object' && 'detail' in j) {
        detail = j.detail
        msg = messageFromDetail(j.detail, msg)
      }
    } catch {
      /* keep text */
    }
    throw new PythonApiError(res.status, msg, detail)
  }
  if (res.status === 204) {
    return undefined as T
  }
  return res.json() as Promise<T>
}

/**
 * Authenticated binary GET against the Python API. Returns the raw response
 * body as a Blob (e.g. for PDF downloads/preview).
 */
export async function pythonApiFetchBlob(
  path: string,
  options: RequestInit = {}
): Promise<Blob> {
  const token = await getJwtToken()
  if (!token) {
    throw new PythonApiError(401, 'Not authenticated')
  }
  const url = joinUrl(path)
  const res = await fetch(url, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(options.headers as Record<string, string>)
    }
  })
  if (!res.ok) {
    const text = await res.text()
    let detail: unknown = text || res.statusText
    let msg = text || res.statusText
    try {
      const j = JSON.parse(text) as { detail?: unknown }
      if (j && typeof j === 'object' && 'detail' in j) {
        detail = j.detail
        msg = messageFromDetail(j.detail, msg)
      }
    } catch {
      /* keep text */
    }
    throw new PythonApiError(res.status, msg, detail)
  }
  return res.blob()
}

/** Public health check (no auth). */
export async function pythonApiHealth(): Promise<{ ok?: boolean; db?: string; app_version?: string }> {
  const base = pythonBaseUrl()
  if (!base) {
    throw new PythonApiError(0, 'REACT_APP_PYTHON_API_URL is not set')
  }
  const res = await fetch(joinUrl('/health'))
  if (!res.ok) {
    throw new PythonApiError(res.status, res.statusText)
  }
  return res.json()
}

/**
 * Mechanical standalone app used `api()` from `config/api.ts`.
 * Same call shape; throws `Error` like the original on failure.
 */
export async function mechApi<T>(path: string, init: RequestInit = {}): Promise<T> {
  try {
    return await pythonApiFetch<T>(path, init)
  } catch (e) {
    if (e instanceof PythonApiError) {
      throw new Error(`${e.status} ${e.message}`)
    }
    throw e
  }
}
